/**
 * KDF: derivación de la CLAVE MAESTRA a partir de la contraseña maestra.
 *
 * Algoritmo: **Argon2id** (ganador del Password Hashing Competition; resiste
 * GPU/ASIC por su coste en memoria y ataques de canal lateral por su modo
 * híbrido). WebCrypto no lo trae, así que usamos `@noble/hashes` (JS puro
 * auditado, sin binarios nativos → funciona en navegador, Bun y Node).
 *
 * Parámetros (OWASP Password Storage Cheat Sheet, perfil interactivo):
 *   - m = 19 MiB (19456 KiB), t = 2, p = 1   → "INTERACTIVE" (móvil/desbloqueo)
 *   - m = 64 MiB (65536 KiB), t = 3, p = 4   → "SENSITIVE"   (creación de cuenta)
 * El `salt` es aleatorio de 16 bytes por usuario y se guarda junto al blob
 * (público: no es secreto). La longitud de salida es 32 bytes (AES-256).
 *
 * INVARIANTE ZERO-KNOWLEDGE: la clave maestra que sale de aquí NUNCA se
 * serializa hacia el servidor. Lo que el servidor recibe es (a) el `authHash`
 * (verificador unidireccional, dominio separado) y (b) blobs cifrados. Ver
 * `account.ts` para la separación wrap-key / auth-hash.
 */

import { argon2id } from "@noble/hashes/argon2.js";
import { hkdfExpand } from "./primitives";

export const MASTER_KEY_BYTES = 32;
export const KDF_SALT_BYTES = 16;

export interface Argon2Params {
  /** Coste en memoria en KiB. */
  readonly m: number;
  /** Iteraciones (time cost). */
  readonly t: number;
  /** Paralelismo (lanes). */
  readonly p: number;
}

export const KDF_PRESETS = {
  /** Desbloqueo frecuente / dispositivos modestos. */
  interactive: { m: 19_456, t: 2, p: 1 },
  /** Creación de cuenta / operaciones de alto valor. */
  sensitive: { m: 65_536, t: 3, p: 4 },
} as const satisfies Record<string, Argon2Params>;

export type KdfPresetName = keyof typeof KDF_PRESETS;

export interface KdfConfig {
  readonly algorithm: "argon2id";
  readonly params: Argon2Params;
  /** salt en base64 (público). */
  readonly salt: string;
  /** Versión del esquema, para migraciones futuras. */
  readonly v: 1;
}

export function newKdfSaltBytes(): Uint8Array {
  const s = new Uint8Array(KDF_SALT_BYTES);
  crypto.getRandomValues(s);
  return s;
}

/**
 * Deriva la clave maestra (32 bytes) desde la contraseña + salt con Argon2id.
 * Es una función pura y determinista para el mismo (password, salt, params).
 */
export function deriveMasterKey(
  password: string,
  salt: Uint8Array,
  params: Argon2Params = KDF_PRESETS.interactive,
): Uint8Array {
  if (salt.length < KDF_SALT_BYTES) {
    throw new Error("salt too short");
  }
  return argon2id(new TextEncoder().encode(password.normalize("NFKC")), salt, {
    m: params.m,
    t: params.t,
    p: params.p,
    dkLen: MASTER_KEY_BYTES,
  });
}

/**
 * Clave de envoltura (wrap key): sub-clave de la maestra dedicada a envolver
 * la clave de cuenta. Dominio separado del auth-hash vía HKDF-info distinto.
 */
export async function deriveMasterWrapKey(masterKey: Uint8Array): Promise<Uint8Array> {
  return hkdfExpand(masterKey, "boveda:e2e-crypto:v1:master-wrap", MASTER_KEY_BYTES);
}

/**
 * Verificador de autenticación para el servidor. Se deriva de la clave maestra
 * con un `info` HKDF DISTINTO al de la wrap key, de modo que:
 *   - el servidor puede comprobar la contraseña (comparando su hash del
 *     authHash) SIN aprender la clave maestra ni la wrap key;
 *   - filtrar el authHash NO revela la wrap key (independencia de dominio).
 * El servidor, además, re-hashea el authHash con su propio salt antes de
 * almacenarlo (nunca guarda el authHash tal cual).
 */
export async function deriveAuthHash(masterKey: Uint8Array): Promise<Uint8Array> {
  return hkdfExpand(masterKey, "boveda:e2e-crypto:v1:auth-verifier", MASTER_KEY_BYTES);
}
